<template>
  <div class="relative" data-testid="card-slider">
    <!-- Left arrow -->
    <button
      v-show="!arrivedState.left"
      aria-label="Scroll left"
      class="absolute left-1 top-1/2 z-10 hidden -translate-y-1/2 aspect-square rounded-full bg-depth-2 p-2.5 text-rhino shadow-xl transition-colors hover:bg-depth-3 hover:text-white xl:flex xl:items-center xl:justify-center"
      @click="scrollByCard(-1)"
    >
      <fa-icon :icon="faChevronLeft" class="size-4" aria-hidden="true" />
    </button>

    <!-- Right arrow -->
    <button
      v-show="!arrivedState.right"
      aria-label="Scroll right"
      class="absolute right-1 top-1/2 z-10 hidden -translate-y-1/2 aspect-square rounded-full bg-depth-2 p-2.5 text-rhino shadow-xl transition-colors hover:bg-depth-3 hover:text-white xl:flex xl:items-center xl:justify-center"
      @click="scrollByCard(1)"
    >
      <fa-icon :icon="faChevronRight" class="size-4" aria-hidden="true" />
    </button>

    <!-- Scrollable track -->
    <div
      ref="trackEl"
      class="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-4 p-4"
      :class="{
        'cursor-grab': !isDragging,
        'cursor-grabbing select-none': isDragging,
      }"
      @dragstart.prevent
      @pointerdown="onDragStart"
      @pointermove.passive="onDragMove"
      @pointerup="onDragEnd"
      @pointercancel="onDragEnd"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { useMutationObserver, useResizeObserver } from '@vueuse/core';

const GAP = 16;
const DRAG_THRESHOLD = 5;

const trackEl = ref<HTMLElement>();
const { arrivedState } = useScroll(trackEl, { offset: { right: 1 } });

// useScroll only recalculates arrivedState on scroll events. Force a
// re-evaluation whenever the track's size changes (images loading) or its
// children change (tab switch replaces cards with same-size new ones).
const refreshArrivedState = () => {
  trackEl.value?.dispatchEvent(new Event('scroll'));
};
useResizeObserver(trackEl, refreshArrivedState);
useMutationObserver(trackEl, refreshArrivedState, {
  childList: true,
  subtree: true,
});

const isDragging = ref(false);
// Id of the pointer that started the drag, or null when idle. The track's
// listeners see every pointer on the page, so a second one (a finger on a
// touch laptop, a hovering pen) must not steer or end someone else's drag.
let activePointerId: number | null = null;
// Handle of the frame that clears isDragging after a drag, 0 when none is
// pending. Kept so a new drag can cancel a previous drag's callback.
let clickGuardFrame = 0;
let scrollStartX = 0;
let dragStartClientX = 0;

// Mouse drag only. Touch and pen are left to the browser so the default
// `touch-action: auto` applies: a horizontal swipe scrolls the track, a
// vertical one scrolls the page and pinch-zoom keeps working — driving those
// from JS as well would double up on the native pan. This is why we no longer
// use VueUse's `usePointerSwipe`: it force-sets an inline `touch-action: none`
// on mount, which traps both gestures inside the track.
function onDragStart(e: PointerEvent) {
  const el = trackEl.value;
  if (!el || e.pointerType !== 'mouse' || e.button !== 0) return;

  // Drop the previous drag's pending release so it can't unset isDragging
  // mid-drag and let this drag's trailing click through.
  cancelAnimationFrame(clickGuardFrame);
  clickGuardFrame = 0;

  activePointerId = e.pointerId;
  isDragging.value = false;
  scrollStartX = el.scrollLeft;
  dragStartClientX = e.clientX;
  el.style.scrollSnapType = 'none';

  // Capture on the event target (not on the track) so the drag survives the
  // pointer leaving the track while the click that follows still resolves to
  // the card underneath. Capture is released implicitly on pointerup/cancel;
  // the retargeted events keep bubbling to the track's own listeners.
  (e.target as Element | null)?.setPointerCapture(e.pointerId);
}

function onDragMove(e: PointerEvent) {
  const el = trackEl.value;
  if (!el || e.pointerId !== activePointerId) return;

  // The pointerup can go missing if the captured element is torn out mid-drag
  // (a tab switch or a refetch replaces every card). Recover on the next move
  // instead of staying stuck in a drag the user already finished.
  if (e.buttons === 0) {
    onDragEnd(e);
    return;
  }

  const delta = e.clientX - dragStartClientX;
  if (!isDragging.value && Math.abs(delta) < DRAG_THRESHOLD) return;

  isDragging.value = true;
  el.scrollLeft = scrollStartX - delta;
}

function onDragEnd(e: PointerEvent) {
  const el = trackEl.value;
  if (!el || e.pointerId !== activePointerId) return;

  activePointerId = null;
  el.style.scrollSnapType = '';

  // Stay "dragging" for one frame so the card's click guard swallows the click
  // that fires at the end of a drag.
  if (isDragging.value) {
    clickGuardFrame = requestAnimationFrame(() => {
      clickGuardFrame = 0;
      isDragging.value = false;
    });
  }
}

provide('isDragging', isDragging);

function scrollByCard(direction: number) {
  const el = trackEl.value;
  if (!el) return;
  const firstChild = el.firstElementChild as HTMLElement | null;
  const step = firstChild ? firstChild.offsetWidth + GAP : 640;
  el.scrollBy({ left: direction * step, behavior: 'smooth' });
}

defineExpose({ trackEl });
</script>
